import React, { useState, useEffect, useRef } from 'react';
 import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Bot,
  Shield,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Loader2,
  MessageSquare,
  Calculator,
  HelpCircle,
  Headphones,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

interface BotInfo {
  id: string;
  tenant_id: string;
  status: string | null;
  governance_pack_id: string | null;
  created_at: string | null;
  widget_settings?: {
    primary_color?: string;
    icon_style?: string;
    position?: string;
    greeting?: string;
  };
}

interface TenantInfo {
  id: string;
  name: string;
  industry: string | null;
}

interface GovernanceInfo {
  public_trust_charter: string | null;
}

interface ApprovalInfo {
  id: string;
  status: string | null;
  feedback_text: string | null;
  created_at: string | null;
}

// Helper function to map icon style to component
const getWidgetIcon = (iconStyle: string | undefined) => {
  switch (iconStyle) {
    case 'calculator':
      return Calculator;
    case 'help':
      return HelpCircle;
    case 'headphones':
      return Headphones;
    case 'bot':
      return Bot;
    case 'chat':
    default:
      return MessageSquare;
  }
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  citations?: Array<{ url: string; title?: string; snippet?: string }>;
  confidence?: number;
  escalated?: boolean;
}

const ClientBotPreview: React.FC = () => {
  const { botId } = useParams<{ botId: string }>();
   const [searchParams] = useSearchParams();
  const navigate = useNavigate();
   const { user, role, tenantId: userTenantId, loading: authLoading, sessionChecked } = useAdminAuth();
   
   // Magic link token from URL
   const magicToken = searchParams.get('token');
  
  const [bot, setBot] = useState<BotInfo | null>(null);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [governance, setGovernance] = useState<GovernanceInfo | null>(null);
  const [approval, setApproval] = useState<ApprovalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
   const [tokenVerified, setTokenVerified] = useState(false);
   const [tokenExpired, setTokenExpired] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Approval form state
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWidgetGreeting, setShowWidgetGreeting] = useState(false);

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch bot and check access
  useEffect(() => {
    let cancelled = false;
    
    console.log('ClientBotPreview effect running:', { 
      botId, 
       sessionChecked, 
      user: !!user, 
      role, 
       magicToken: !!magicToken
    });

    const fetchData = async () => {
      if (!botId) return;
      
       // If using magic token, proceed directly
       if (magicToken) {
         // Magic token path - no auth needed
       }
       // Wait for session to be checked before making any decisions (auth hook now has retry logic)
       else if (!sessionChecked) {
         console.log('ClientBotPreview: waiting for session check');
        return;
      }
       // Session has been checked - now we can make access decisions
       else if (!user) {
         console.log('ClientBotPreview: session checked, no user - access denied');
         setAccessDenied(true);
         setLoading(false);
         return;
      }

      // --- Now we're ready to fetch - set loading state ---
      console.log('ClientBotPreview: auth ready, proceeding to fetch');
      console.log('ClientBotPreview: current role =', role, 'userTenantId =', userTenantId);
      setAccessDenied(false);
      setLoading(true);

      // If there's a magic token, verify it first (no auth required)
      if (magicToken) {
        try {
          const { data: linkData, error: linkError } = await supabase
            .from('bot_preview_links')
            .select('*')
            .eq('token', magicToken)
            .eq('bot_id', botId)
            .single();

          if (cancelled) return;

          if (linkError || !linkData) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }

          // Check if token has expired
          if (new Date(linkData.expires_at) < new Date()) {
            setTokenExpired(true);
            setAccessDenied(true);
            setLoading(false);
            return;
          }

          // Token is valid - mark as verified and continue
          setTokenVerified(true);

          // Mark token as used (optional - you may want one-time use)
          await supabase
            .from('bot_preview_links')
            .update({ used_at: new Date().toISOString() })
            .eq('id', linkData.id);

        } catch (error) {
          console.error('Error verifying magic link:', error);
          if (!cancelled) {
            setAccessDenied(true);
            setLoading(false);
          }
          return;
        }
       }

      try {
        // Fetch bot details
        const { data: botData, error: botError } = await supabase
          .from('bots')
          .select('id, tenant_id, status, governance_pack_id, created_at, widget_settings')
          .eq('id', botId)
          .single();

        if (cancelled) return;

        if (botError) throw botError;
        if (!botData) {
          setLoading(false);
          return;
        }

         // Check access: magic token (already verified above) OR staff OR client_viewer
         const hasMagicAccess = !!magicToken; // If we got here with magicToken, it was verified
         
         // Use the role directly from dependency, not from closure
         const currentRole = role;
         const isStaff = currentRole === 'admin' || currentRole === 'operator';
         const isClientViewer = currentRole === 'client_viewer' && userTenantId === botData.tenant_id;
         
         console.log('ClientBotPreview access check:', { 
           currentRole, 
           hasMagicAccess, 
           isStaff, 
           isClientViewer,
           userTenantId,
           botTenantId: botData.tenant_id 
         });

         if (!hasMagicAccess && !isStaff && !isClientViewer) {
          console.log('Access DENIED - none of the conditions passed');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        console.log('Access granted! Loading bot data...');
      if (!cancelled) {
        setBot({
          ...botData,
          widget_settings: botData.widget_settings as BotInfo['widget_settings'],
        });
      }

        // Fetch related data in parallel
        const [tenantRes, governanceRes, approvalRes] = await Promise.all([
          supabase.from('advisory_tenants').select('id, name, industry').eq('id', botData.tenant_id).single(),
          botData.governance_pack_id
            ? supabase.from('governance_packs').select('public_trust_charter').eq('id', botData.governance_pack_id).single()
            : Promise.resolve({ data: null }),
          supabase.from('approvals').select('*').eq('bot_id', botId).order('created_at', { ascending: false }).limit(1).single(),
        ]);

        if (tenantRes.data) setTenant(tenantRes.data);
        if (governanceRes.data) setGovernance(governanceRes.data);
        if (approvalRes.data) setApproval(approvalRes.data);

        if (cancelled) return;

        // Add welcome message
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: `Hello! I'm the AI assistant for ${tenantRes.data?.name || 'this firm'}. This is a preview of how I'll interact with your clients. Feel free to test my responses!`,
          timestamp: new Date(),
        }]);

      } catch (error) {
        console.error('Error fetching bot data:', error);
        toast.error('Failed to load bot preview');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      cancelled = true;
    };
    }, [botId, sessionChecked, user, role, userTenantId, magicToken]);

  // Real chat endpoint call
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isChatLoading || !bot) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: chatInput.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = chatInput.trim();
    setChatInput('');
    setIsChatLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages
        .filter(m => m.id !== 'welcome')
        .map(m => ({ role: m.role, content: m.content }));

      const { data, error } = await supabase.functions.invoke('advisory-chat', {
        body: {
          bot_id: bot.id,
          message: messageText,
          conversation_history: conversationHistory,
          session_id: sessionId,
        },
      });

      if (error) {
        console.error('Chat API error:', error);
        toast.error('Failed to get response');
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: "I'm sorry, I encountered an error processing your request. Please try again.",
          timestamp: new Date(),
        }]);
        return;
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.answer || "I'm sorry, I couldn't generate a response.",
        timestamp: new Date(),
        citations: data.citations,
        confidence: data.confidence,
        escalated: data.escalated,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Show escalation notice if triggered
      if (data.escalated) {
        toast.info('This conversation has been escalated to the team', {
          description: `Reason: ${data.escalation_reason}`,
        });
      }
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Failed to send message');
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again.",
        timestamp: new Date(),
      }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleApprove = async () => {
    // Allow approval if user is authenticated OR magic token is verified
    if (!bot || (!user && !tokenVerified)) return;
    setIsSubmitting(true);

    try {
      // Create or update approval record
      if (approval) {
        await supabase
          .from('approvals')
          .update({ status: 'approved', feedback_text: null })
          .eq('id', approval.id);
      } else {
        await supabase
          .from('approvals')
          .insert({ bot_id: bot.id, status: 'approved' });
      }

      // Update bot status
      await supabase
        .from('bots')
        .update({ status: 'approved' })
        .eq('id', bot.id);

      setBot({ ...bot, status: 'approved' });
      setApproval(prev => prev ? { ...prev, status: 'approved', feedback_text: null } : { id: 'new', status: 'approved', feedback_text: null, created_at: new Date().toISOString() });
      
      toast.success('Bot approved! It will now proceed to publishing.');
    } catch (error) {
      console.error('Error approving bot:', error);
      toast.error('Failed to approve bot');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestChanges = async () => {
    // Allow changes request if user is authenticated OR magic token is verified
    if (!bot || (!user && !tokenVerified) || !feedbackText.trim()) {
      toast.error('Please provide feedback about the changes needed');
      return;
    }
    setIsSubmitting(true);

    try {
      // Create or update approval record
      if (approval) {
        await supabase
          .from('approvals')
          .update({ status: 'changes_requested', feedback_text: feedbackText.trim() })
          .eq('id', approval.id);
      } else {
        await supabase
          .from('approvals')
          .insert({ bot_id: bot.id, status: 'changes_requested', feedback_text: feedbackText.trim() });
      }

      // Update bot status
      await supabase
        .from('bots')
        .update({ status: 'changes_requested' })
        .eq('id', bot.id);

      setBot({ ...bot, status: 'changes_requested' });
      setApproval(prev => prev 
        ? { ...prev, status: 'changes_requested', feedback_text: feedbackText.trim() } 
        : { id: 'new', status: 'changes_requested', feedback_text: feedbackText.trim(), created_at: new Date().toISOString() }
      );
      setFeedbackText('');
      
      toast.success('Feedback submitted. Our team will review and make adjustments.');
    } catch (error) {
      console.error('Error requesting changes:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

   // Show loading while waiting for auth (skip auth check if magic token present)
   if ((!sessionChecked && !magicToken) || loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-[500px]" />
            <Skeleton className="h-[500px]" />
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
             <h2 className="text-xl font-semibold mb-2">
               {tokenExpired ? 'Link Expired' : 'Access Denied'}
             </h2>
            <p className="text-muted-foreground mb-4">
               {tokenExpired 
                 ? 'This preview link has expired. Please contact the administrator for a new link.'
                 : "You don't have permission to view this bot preview."
               }
            </p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Bot Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The bot preview you're looking for doesn't exist.
            </p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isApproved = bot.status === 'approved' || bot.status === 'live';
  const canApprove = bot.status === 'ready_for_review' || bot.status === 'changes_requested';
  const isClientViewer = role === 'client_viewer';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">{tenant?.name || 'Bot'} Preview</h1>
                <p className="text-sm text-muted-foreground">
                  Review and approve your AI assistant
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge 
                className={
                  bot.status === 'approved' || bot.status === 'live' 
                    ? 'bg-green-500/10 text-green-600'
                    : bot.status === 'changes_requested'
                    ? 'bg-orange-500/10 text-orange-600'
                    : 'bg-amber-500/10 text-amber-600'
                }
              >
                {(bot.status || 'draft').replace(/_/g, ' ')}
              </Badge>
              {(role === 'admin' || role === 'operator') && (
                <Link to={`/admin/bots/${bot.id}`}>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Admin View
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chat Preview */}
          <Card className="flex flex-col h-[600px]">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Chat Preview
              </CardTitle>
              <CardDescription>
                Test how your assistant will respond to client questions
              </CardDescription>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-4 bg-muted/30">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-tr-none'
                          : 'bg-card border shadow-sm rounded-tl-none'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">AI</AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-medium">Assistant</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      
                      {/* Show citations for assistant messages */}
                      {message.role === 'assistant' && message.citations && message.citations.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border/50">
                          <p className="text-xs text-muted-foreground mb-1">Sources:</p>
                          <div className="flex flex-wrap gap-1">
                            {message.citations.map((citation, idx) => (
                              <a
                                key={idx}
                                href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {citation.title || `Source ${idx + 1}`}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Show metadata for staff viewing */}
                      {message.role === 'assistant' && (role === 'admin' || role === 'operator') && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-2 flex-wrap">
                          {message.confidence !== undefined && (
                            <Badge variant="outline" className="text-xs">
                              Confidence: {Math.round(message.confidence * 100)}%
                            </Badge>
                          )}
                          {message.escalated && (
                            <Badge className="text-xs bg-orange-500/10 text-orange-600 border-orange-200">
                              Escalated
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs opacity-60 mt-1 text-right">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-card border shadow-sm rounded-lg rounded-tl-none p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">AI</AvatarFallback>
                        </Avatar>
                        <span className="text-xs font-medium">Assistant</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:-0.3s]" />
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:-0.15s]" />
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </CardContent>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="Type a test message..."
                  className="min-h-[60px] resize-none"
                  disabled={isChatLoading}
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={isChatLoading || !chatInput.trim()}
                  className="h-[60px] w-[60px]"
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Approval & Trust Charter */}
          <div className="space-y-6">
            {/* Trust Charter */}
            {governance?.public_trust_charter && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Public Trust Charter
                  </CardTitle>
                  <CardDescription>
                    Governance commitments for this assistant
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {governance.public_trust_charter}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Widget Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Widget Preview
                </CardTitle>
                <CardDescription>
                  How the chat widget will appear on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative border rounded-lg bg-gradient-to-br from-muted/30 to-muted min-h-[200px] overflow-hidden">
                  {/* Mock browser chrome */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted border-b">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 mx-2">
                      <div className="bg-background rounded px-2 py-0.5 text-xs text-muted-foreground truncate">
                        yourwebsite.com
                      </div>
                    </div>
                  </div>
                  
                  {/* Mock website content */}
                  <div className="p-4 space-y-3">
                    <div className="h-4 w-24 bg-muted-foreground/20 rounded" />
                    <div className="space-y-1.5">
                      <div className="h-2 w-full bg-muted-foreground/20 rounded" />
                      <div className="h-2 w-4/5 bg-muted-foreground/20 rounded" />
                    </div>
                  </div>

                  {/* Floating widget icon */}
                  {(() => {
                    const IconComponent = getWidgetIcon(bot.widget_settings?.icon_style);
                    const position = bot.widget_settings?.position || 'bottom-right';
                    const positionClasses = position === 'bottom-left' 
                      ? 'bottom-3 left-3' 
                      : 'bottom-3 right-3';
                    
                    return (
                      <div className={`absolute ${positionClasses} flex flex-col items-end gap-2`}>
                        {showWidgetGreeting && bot.widget_settings?.greeting && (
                          <div className="max-w-[180px] p-2.5 bg-card rounded-lg shadow-lg border text-xs animate-in fade-in slide-in-from-bottom-2">
                            {bot.widget_settings.greeting}
                          </div>
                        )}
                        
                        <button
                          onClick={() => setShowWidgetGreeting(!showWidgetGreeting)}
                          className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
                          style={{ backgroundColor: bot.widget_settings?.primary_color || '#14B8A6' }}
                        >
                          <IconComponent className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* Widget settings summary */}
                <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <div 
                      className="w-3 h-3 rounded border"
                      style={{ backgroundColor: bot.widget_settings?.primary_color || '#14B8A6' }}
                    />
                    <span>Brand color</span>
                  </div>
                  <span>•</span>
                  <span className="capitalize">{bot.widget_settings?.icon_style || 'chat'} icon</span>
                  <span>•</span>
                  <span className="capitalize">{(bot.widget_settings?.position || 'bottom-right').replace('-', ' ')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Approval Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isApproved ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  Approval Status
                </CardTitle>
                <CardDescription>
                  {isApproved
                    ? 'This bot has been approved'
                    : canApprove
                    ? 'Review and approve or request changes'
                    : 'This bot is not yet ready for review'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current status display */}
                {approval?.status === 'changes_requested' && approval.feedback_text && (
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <p className="text-sm font-medium text-orange-700 mb-1">Previous Feedback:</p>
                    <p className="text-sm text-orange-600">{approval.feedback_text}</p>
                  </div>
                )}

                {isApproved ? (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <p className="font-medium text-green-700">Approved</p>
                    <p className="text-sm text-green-600">
                      {bot.status === 'live' ? 'This bot is now live' : 'Ready for publishing'}
                    </p>
                  </div>
                ) : canApprove ? (
                  <>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleApprove}
                        disabled={isSubmitting}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Approve
                      </Button>
                    </div>

                    <Separator />

                    <div className="space-y-3">
                      <p className="text-sm font-medium">Or request changes:</p>
                      <Textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Describe the changes you'd like..."
                        className="min-h-[100px]"
                      />
                      <Button
                        variant="outline"
                        onClick={handleRequestChanges}
                        disabled={isSubmitting || !feedbackText.trim()}
                        className="w-full"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        Request Changes
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="p-4 rounded-lg bg-muted text-center">
                    <p className="text-muted-foreground">
                      This bot needs to pass QA before it can be reviewed.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientBotPreview;
